import React, { useState, useEffect, useContext } from "react";

import classNames from "classnames";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import Markdown from "../../styles/Markdown";

const StyledSlideshow = styled.div`
  position: relative;
  width: 100%;
  min-height: ${({ $height }) => $height || "450px"};
  overflow: hidden;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor || "transparent"};
  ${(props) => props.$dynamicStyles}
`;

const StyledImage = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
`;

const StyledTextOverlay = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
  z-index: 10;
`;

function ImageSlideshow() {
  const {
    text,
    image,
    interval = 5000,
    minHeight = "100vh",
    showOverlay = true,
    overlayBackground = "rgba(0, 0, 0, 0.25)",
    CSS,
    wrapperClassName,
    fullWidth = false,
    backgroundColor,
  } = useContext(ComponentContext);

  const [currentIndex, setCurrentIndex] = useState(0);

  const rowClassName = classNames({ row: !fullWidth }, wrapperClassName);

  // v5 format: image might be array directly or have .data property
  const imageArray = Array.isArray(image) ? image : image?.data || [];

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % imageArray.length);
    }, interval);

    return () => clearInterval(intervalId);
  }, [imageArray.length, interval]);

  return (
    <StyledSlideshow
      className={rowClassName}
      $dynamicStyles={CSS}
      $height={minHeight}
      $backgroundColor={backgroundColor}
    >
      <AnimatePresence>
        {imageArray.map((img, index) => (
          <StyledImage
            key={img?.url || img?.id || index}
            style={{
              backgroundImage: `url(${img?.url})`,
              opacity: currentIndex === index ? 1 : 0,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: currentIndex === index ? 1 : 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1 }}
          />
        ))}
      </AnimatePresence>
      {text && (
        <StyledTextOverlay>
          <Markdown>{text}</Markdown>
        </StyledTextOverlay>
      )}
      {showOverlay && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: overlayBackground,
          }}
        />
      )}
    </StyledSlideshow>
  );
}

export default ImageSlideshow;
